import type { Item, Vec2 } from '../core/hp/types';
import { fixed, t } from '../i18n/I18n';

/**
 * The value beside the cursor while a wall is drawn or dragged, a piece resized or turned (Figma «Value chip» 1069:15665).
 * One element, written straight to the DOM on each move: no React render per pointer move.
 */
export type ChipValue = { kind: 'length'; metres: number } | { kind: 'size'; w: number; d: number } | { kind: 'angle'; rotation: number };

export function chipText(value: ChipValue): string {
  if (value.kind === 'length') return t('#ValueMeters', fixed(value.metres, 2));
  if (value.kind === 'size') return t('#ViewPlanSizeMeters', fixed(value.w, 2), fixed(value.d, 2));
  // The inspector's angle: the turn counted the other way, in (−180°, 180°].
  const degrees = ((Math.round(-value.rotation * 180 / Math.PI) % 360) + 360) % 360;
  return t('#WebGestureChipDegrees', degrees > 180 ? degrees - 360 : degrees);
}

/** A piece being turned reads its angle; resized, its width × depth, or its height while the height handle is pulled. */
export function itemChip(gesture: 'resize' | 'rotate', item: Pick<Item, 'rotation' | 'size'>, axis?: 'w' | 'd' | 'h'): ChipValue {
  if (gesture === 'rotate') return { kind: 'angle', rotation: item.rotation };
  return axis === 'h' ? { kind: 'length', metres: item.size.h } : { kind: 'size', w: item.size.w, d: item.size.d };
}

export interface PlanChipState {
  /** The wall or room being drawn, as the plan draws its draft. */
  draft: { start: Vec2; end: Vec2; kind?: 'room' | 'opening'; pending?: boolean } | null;
  /** The wall a corner or body drag moves. */
  wall?: { length: number };
  /** The piece a resize or turn drag shapes. */
  item?: ChipValue;
}

/** The plan's chip: a drawn wall's length or a drawn room's width × depth, as their canvas labels, else the dragged wall or piece. */
export function planChip({ draft, wall, item }: PlanChipState): ChipValue | null {
  if (draft && draft.kind !== 'opening' && !draft.pending) {
    const w = Math.abs(draft.end.x - draft.start.x), d = Math.abs(draft.end.z - draft.start.z), span = Math.hypot(w, d);
    if (span > 1e-6) return draft.kind === 'room' ? { kind: 'size', w, d } : { kind: 'length', metres: span };
  }
  return wall ? { kind: 'length', metres: wall.length } : item ?? null;
}

const OFFSET_X = 26, OFFSET_Y = 32, MARGIN = 8, HEIGHT = 23;

/** The chip's top-left for a hotspot at (x, y): beside the cursor, mirrored to its left or above it near the window's edges. */
export function chipPlacement(x: number, y: number, width: number, height: number, viewWidth: number, viewHeight: number): { x: number; y: number } {
  const left = x + OFFSET_X + width > viewWidth - MARGIN ? x - OFFSET_X - width : x + OFFSET_X;
  const top = y + OFFSET_Y + height > viewHeight - MARGIN ? y - OFFSET_Y - height : y + OFFSET_Y;
  return { x: Math.max(MARGIN, left), y: Math.max(MARGIN, top) };
}

let chip: HTMLElement | null = null, width = 0;

export function showGestureChip(text: string, clientX: number, clientY: number): void {
  if (typeof document === 'undefined') return;
  if (!chip) {
    chip = document.createElement('div');
    chip.className = 'gesture-chip';
    chip.setAttribute('aria-hidden', 'true');
    document.body.append(chip);
  }
  chip.hidden = false;
  if (chip.textContent !== text) { chip.textContent = text; width = chip.offsetWidth; }
  const at = chipPlacement(clientX, clientY, width, HEIGHT, window.innerWidth, window.innerHeight);
  chip.style.transform = `translate(${at.x}px, ${at.y}px)`;
}

export function hideGestureChip(): void {
  if (chip && !chip.hidden) chip.hidden = true;
}
